import { useLocation } from 'react-router-dom';

import { AccountIntent, useActivation } from '../ActivationContext';

import { isSafeRedirectUri } from '@/utils/urlHelpers';
import {
  REDIRECT_TO_SESSION_KEY,
  CANOPY_SIGN_UP_CALLBACK,
  CANOPY_LOGIN_CALLBACK,
} from '@/utils/constants';

/**
 * Where to send the member's device once authentication finishes.
 *
 * **Why this exists as a hook rather than a field on the flow's state.** The app opens
 * the portal inside an in-app browser and watches for it to navigate to a private URL
 * scheme carrying a one-time code. If that address is lost anywhere along the way, the
 * member completes every step successfully and the app never finds out — they are left
 * looking at a browser that goes nowhere.
 *
 * The flow's in-memory state is not a reliable place to keep it. A member who arrives
 * by following a link from their email lands in a **fresh browser tab**, so the state
 * built up before the email was sent is gone, and the deep link with it. That is
 * exactly the case the screens after email verification run in.
 *
 * So the address is looked for in four places, most specific first: this screen's own
 * URL, the copy saved when the member first arrived, whatever the flow's state still
 * holds, and finally the app's standard address for this journey. Every candidate is
 * checked before it is trusted — an unvalidated redirect target is an open redirect.
 *
 * The last of those four is what makes this safe to rely on: there is always an answer,
 * so no screen in the middle of the flow can silently drop the hand-off.
 */
// eslint-disable-next-line import/prefer-default-export
export const usePostAuthRedirectTo = (): string => {
  const { search } = useLocation();
  const { state } = useActivation();

  const fromUrl = new URLSearchParams(search).get('redirectTo');
  if (fromUrl && isSafeRedirectUri(fromUrl)) { return fromUrl; }

  // Guarded because this runs in a render path. A browser with storage locked down
  // throws on access, and there is no error boundary in this app — an unguarded read
  // here blanks the screen mid-flow. Treat unreadable storage as "no saved value" and
  // fall through to the remaining candidates, the same way ResetPassword does.
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);
  } catch {
    stored = null;
  }
  if (stored && isSafeRedirectUri(stored)) { return stored; }

  if (state.redirectTo && isSafeRedirectUri(state.redirectTo)) { return state.redirectTo; }

  return state.accountIntent === AccountIntent.Signin
    ? CANOPY_LOGIN_CALLBACK
    : CANOPY_SIGN_UP_CALLBACK;
};
